import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { faPause, faPlay } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { useStableCallback } from '@soundxyz/graphql-react-query/utils';
import { PlayIndicatorBars } from '@components/main-components/cards/PlayIndicatorBars';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { ListeningPartyTooltip } from '@components/main-components/play-button/PlayButton';
import { type TopReleasePostFragment } from '@graphql/reactQuery';
import { useHover } from '@hooks/useHover';
import { useCurrentListeningParty, usePlayButton } from '@hooks/usePlayButton';
import { useAudioController } from '@store/audio/AudioController';

export const ReleasePlayButton = ({
  release,
}: {
  release: TopReleasePostFragment['optionalRelease'];
}) => {
  const { activeTrackId, playing } = useAudioController();
  const [hovering, hoverProps] = useHover();

  const isTrackPlaying = activeTrackId === release?.track.id && playing;

  const currentListeningParty = useCurrentListeningParty({
    listeningParty: release?.listeningParty,
    releaseId: release?.id,
  });

  const { onPlayClick } = usePlayButton({
    releaseId: release?.id,
    isAlbum: false,
    trackId: release?.track.id ?? null,
    listeningParty: null,
  });

  const handlePlay = useStableCallback(() => {
    if (!currentListeningParty) {
      onPlayClick();
    }
  });

  if (!release) return null;

  const buttonClass = clsx(
    hovering ? 'text-base800' : 'text-base600',
    currentListeningParty && 'cursor-not-allowed text-base800 opacity-30',
  );

  const playButton = (
    <IconButton
      className="h-3 w-3 [&_svg]:h-3"
      icon={
        <FontAwesomeIcon
          className={clsx('text-base-s', buttonClass)}
          icon={isTrackPlaying ? faPause : faPlay}
        />
      }
      animate
    />
  );

  const releaseLine = (
    <div
      className={clsx(
        'flex w-fit items-center gap-1.5 pb-1 text-base600 transition-colors hover:text-base800',
        currentListeningParty && '!cursor-not-allowed',
      )}
      onClick={handlePlay}
      {...hoverProps}
    >
      {playButton}
      {isTrackPlaying && <PlayIndicatorBars smallBars />}
      <div className="line-clamp-1 w-fit text-base-s font-semibold">
        {release?.artist.name} - {release?.title}
      </div>
    </div>
  );

  if (currentListeningParty) {
    return (
      <ListeningPartyTooltip listeningParty={currentListeningParty}>
        {releaseLine}
      </ListeningPartyTooltip>
    );
  }

  return releaseLine;
};
